import React, { useRef, useEffect } from 'react';
import { Box, Popover, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import esES from 'antd/locale/es_ES';
import enUS from 'antd/locale/en_US';
import itIT from 'antd/locale/it_IT';
import { theme, ThemedTypography, ThemedButton } from '../../themes/technicalAlerts';
import SharedDatePicker from './Shared/SharedDatePicker';

// Base popover wrapper with consistent styling
const BasePopover = ({
  open,
  anchorEl,
  onClose,
  children,
  minWidth = 160,
  placement = { vertical: 'bottom', horizontal: 'center' }
}) => {
  // Ref for popover scroll container
  const popoverRef = useRef(null);

  // Popover is in a portal (outside main scroll container).
  // MUI components capture wheel events for scroll-to-change behavior.
  // Solution: Intercept in capture phase, stop propagation, manually scroll page.
  useEffect(() => {
    const popoverElement = popoverRef.current;
    if (!popoverElement || !open) return;

    const handleWheel = (e) => {
      if (!popoverElement.contains(e.target)) return;

      // CRITICAL: Stop event from reaching MUI components that would block scroll
      e.stopPropagation();

      // Manually scroll the main page content
      const pageScrollContainer = document.querySelector('.css-4z6nar') || document.documentElement;
      pageScrollContainer.scrollTop += e.deltaY;
    };

    // Use capture phase to intercept before MUI's handlers
    popoverElement.addEventListener('wheel', handleWheel, { capture: true, passive: false });

    return () => {
      popoverElement.removeEventListener('wheel', handleWheel, { capture: true });
    };
  }, [open]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={placement}
      transformOrigin={{
        vertical: 'top',
        horizontal: placement.horizontal,
      }}
      PaperProps={{
        ref: popoverRef
      }}
      sx={{
        zIndex: 1500, // Higher than theme.zIndex.popover to ensure it's above everything
        '& .MuiPaper-root': {
          overflow: 'visible',
          zIndex: 1500
        },
        '& .MuiPopover-paper': {
          maxWidth: 'calc(100vw - 16px)'
        }
      }}
    >
      <Box sx={{ p: 2, minWidth }}>
        {children}
      </Box>
    </Popover>
  );
};

// Confirmation popover for Yes/No questions
export const ConfirmationPopover = ({
  open,
  anchorEl,
  onClose,
  title,
  onConfirm,
  onCancel,
  confirmText = 'yes',
  cancelText = 'no',
  confirmColor = 'error'
}) => {
  const { t } = useTranslation();
  
  return (
    <BasePopover open={open} anchorEl={anchorEl} onClose={onClose}>
      <Box sx={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'center',
        alignItems: 'center',
        py: '4px'
      }}>
        {/* Question text inline with buttons - matching card layout */}
        <ThemedTypography.Caption sx={{
          fontSize: '10px',
          color: theme.colors.text.primary,
          fontWeight: 500,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          mr: '4px'
        }}>
          {t(title)}
        </ThemedTypography.Caption>
        
        {/* Buttons styled to match card IconButtons */}
        <Button 
          size="small" 
          variant="outlined"
          onClick={onCancel}
          sx={{
            minWidth: '24px',
            width: '24px',
            height: '24px',
            backgroundColor: 'transparent',
            border: '1px solid rgba(107, 114, 128, 0.2)', // Subtle gray border like card
            borderRadius: '4px', // Smaller radius like card
            color: '#8E8E93', // Apple gray like card
            fontSize: '9px',
            fontWeight: 500,
            padding: 0,
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.05)',
              borderColor: 'rgba(34, 197, 94, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.98)'
            }
          }}
        >
          {t(cancelText)}
        </Button>
        <Button 
          size="small" 
          variant="outlined"
          onClick={onConfirm}
          sx={{
            minWidth: '24px',
            width: '24px',
            height: '24px',
            backgroundColor: 'transparent',
            border: '1px solid rgba(107, 114, 128, 0.2)', // Subtle gray border like card
            borderRadius: '4px', // Smaller radius like card
            color: '#8E8E93', // Apple gray like card
            fontSize: '9px',
            fontWeight: 500,
            padding: 0,
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.05)',
              borderColor: 'rgba(249, 115, 22, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.98)'
            }
          }}
        >
          {t(confirmText)}
        </Button>
      </Box>
    </BasePopover>
  );
};

// Date selection popover with DatePicker
export const DateSelectionPopover = ({
  open,
  anchorEl,
  onClose,
  title,
  selectedDate,
  onDateChange,
  onSubmit,
  onCancel,
  submitText = 'submitFixDate',
  cancelText = 'cancel',
  loading = false,
  showCancelOption = false,
  cancelOptionText = 'notFixedYet'
}) => {
  const { t, i18n } = useTranslation();
  const locale = { es: esES, en: enUS, it: itIT }[i18n.language] || esES;
  
  return (
    <BasePopover open={open} anchorEl={anchorEl} onClose={onClose} minWidth={220}>
      <Box sx={{
        p: '12px',
        backgroundColor: 'rgba(249, 250, 251, 0.8)', // Very subtle background like card
        borderRadius: '6px',
        border: '1px solid rgba(107, 114, 128, 0.2)',
        mx: '-8px', // Counteract BasePopover padding
        my: '-8px'
      }}>
        {/* Question prompt - matching card styling */}
        <ThemedTypography.Caption sx={{
          fontSize: '10px',
          color: theme.colors.text.primary,
          fontWeight: 500,
          textAlign: 'center',
          mb: '8px',
          lineHeight: 1.2,
          display: 'block'
        }}>
          {t(title)}
        </ThemedTypography.Caption>

        {/* Date picker - matching card styling */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          mb: '8px'
        }}>
          <SharedDatePicker
            selectedDate={selectedDate}
            onChange={onDateChange}
            locale={locale}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            style={{ 
              width: '140px',
              borderColor: 'rgba(107, 114, 128, 0.3)',
              borderRadius: '4px',
              fontSize: '10px'
            }}
            t={t}
          />
        </Box>
        
        {/* Action buttons - matching card styling */}
        <Box sx={{
          display: 'flex',
          gap: '6px',
          justifyContent: 'center'
        }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              minWidth: 'auto',
              px: '8px',
              py: '2px',
              fontSize: '10px', // Match card
              fontWeight: 500,
              color: '#8E8E93', // Apple gray
              backgroundColor: 'transparent',
              border: '1px solid rgba(142, 142, 147, 0.2)',
              borderRadius: '6px', // More rounded like card
              textTransform: 'none',
              transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              '&:hover': {
                backgroundColor: 'rgba(142, 142, 147, 0.08)',
                borderColor: 'rgba(142, 142, 147, 0.3)'
              },
              '&:disabled': {
                opacity: 0.4
              }
            }}
          >
            {t(cancelText)}
          </Button>

          <Button
            onClick={onSubmit}
            disabled={!selectedDate || loading}
            sx={{
              minWidth: 'auto',
              px: '12px',
              py: '2px',
              fontSize: '10px', // Match card font size
              fontWeight: 500, // Match card font weight
              color: 'white',
              backgroundColor: '#007AFF', // Apple blue
              border: '1px solid #007AFF',
              borderRadius: '6px', // More rounded like card
              textTransform: 'none',
              transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              '&:hover': {
                backgroundColor: '#0051D0',
                borderColor: '#0051D0',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0, 122, 255, 0.25)'
              },
              '&:disabled': {
                opacity: 0.4,
                backgroundColor: '#007AFF',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {loading ? 
              (t('planning') || 'Planning...') : 
              (t(submitText) || 'Submit')
            }
          </Button>
        </Box>
      </Box>
    </BasePopover>
  );
};

// Generic action popover with custom content
export const ActionPopover = ({
  open,
  anchorEl,
  onClose,
  title,
  children,
  actions = [],
  minWidth = 160
}) => {
  const { t } = useTranslation();
  
  return (
    <BasePopover open={open} anchorEl={anchorEl} onClose={onClose} minWidth={minWidth}>
      {title && (
        <ThemedTypography.Caption sx={{ mb: 1.5, fontWeight: 600 }}>
          {t(title)}
        </ThemedTypography.Caption>
      )}
    
    {children}
    
    {actions.length > 0 && (
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {actions.map((action, index) => (
          <Button
            key={index}
            size="small"
            variant={action.variant || 'outlined'}
            color={action.color || 'primary'}
            onClick={action.onClick}
            disabled={action.disabled}
            sx={{ 
              ...theme.typography.micro,
              ...(action.sx || {})
            }}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    )}
    </BasePopover>
  );
};

// Hook for managing popover state
export const usePopoverState = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [popoverType, setPopoverType] = React.useState(null);
  const [popoverData, setPopoverData] = React.useState({});

  const openPopover = React.useCallback((event, type, data = {}) => {
    setAnchorEl(event.currentTarget);
    setPopoverType(type);
    setPopoverData(data);
  }, []);

  const closePopover = React.useCallback(() => {
    setAnchorEl(null);
    setPopoverType(null);
    setPopoverData({});
  }, []);

  const isOpen = (type = null) => {
    if (type) return Boolean(anchorEl) && popoverType === type;
    return Boolean(anchorEl);
  };

  return {
    anchorEl,
    popoverType,
    popoverData,
    openPopover,
    closePopover,
    isOpen
  };
};

// Pre-configured popovers for common Technical Alerts use cases
export const TechnicalAlertsPopovers = {
  // Review popover for broken slot confirmation
  Review: ({ open, anchorEl, onClose, onConfirmBroken, onConfirmNotBroken }) => (
    <ConfirmationPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      title="isThisBroken"
      onConfirm={() => onConfirmBroken?.()}
      onCancel={() => onConfirmNotBroken?.()}
      confirmText="yes"
      cancelText="no"
      confirmColor="error"
    />
  ),

  // Plan fix date popover (for confirming broken with planned date)
  PlanDate: ({ 
    open, 
    anchorEl, 
    onClose, 
    selectedDate, 
    onDateChange, 
    onSubmit, 
    loading = false
  }) => (
    <DateSelectionPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      title="whenWillYouFixIt"
      selectedDate={selectedDate}
      onDateChange={onDateChange}
      onSubmit={onSubmit}
      submitText="planRepair"
      loading={loading}
      showCancelOption={false}
    />
  ),

  // Fix date popover for setting resolution date
  FixDate: ({ 
    open, 
    anchorEl, 
    onClose, 
    selectedDate, 
    onDateChange, 
    onSubmit, 
    loading = false
  }) => (
    <DateSelectionPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      title="whenWasItFixed"
      selectedDate={selectedDate}
      onDateChange={onDateChange}
      onSubmit={onSubmit}
      submitText="markAsRepaired"
      loading={loading}
    />
  )
};

export default BasePopover;
